class Base {
	constructor() {
		this.listeners = new Set();
		this.bound = false;
	}
	notify() {
		const value = this.get();
		for (const listener of this.listeners) {
			listener(value);
		}
	}
	on(listener, initial = true) {
		this.listeners.add(listener);
		// just to trigger the getter, for the `Lazy` below
		const value = this.get();
		if (initial) {
			listener(value);
		}
		if (!this.bound) {
			if (this.bind) { this.bind(); }
			this.bound = true;
		}
		return this;
	}
	off(listener) {
		this.listeners.delete(listener);
		if (!this.listeners.size) {
			if (this.unbind) { this.unbind(); }
			this.bound = false;
		}
		return this;
	}
}

// This represents a property that has a current value and can change over time
class Property extends Base {
	constructor(value) {
		super();
		this.value = value;
	}
	set(value) {
		if (this.value === value) { return this; }
		this.value = value;
		this.notify();
		return this;
	}
	get() {
		return this.value;
	}
}

// This will always contain the value of the latest `Property` which has been
// `set` on this.
class Switch extends Base {
	constructor(source) {
		super();
		this.source = source;
		this.notify = this.notify.bind(this);
	}
	get() {
		return this.source.get();
	}
	set(source) {
		const value = this.get();
		this.source.off(this.notify);
		this.source = source;
		if (this.bound) {
			this.source.on(this.notify, value !== this.get());
		}
	}
	bind() {
		this.source.on(this.notify, false);
	}
	unbind() {
		this.source.off(this.notify);
	}
}

// This will create a lazy property with an optional `initial` value that
// defaults to `null`. The first argument is a function that when invoked
// should return a Promise.
class Lazy extends Base {
	constructor(fn, initial = null) {
		super();
		this.value = initial;
		this.requestFn = fn;
		this.running = false;
	}
	get() {
		if (!this.running) {
			this.running = true;
			const set = value => {
				this.value = value;
				this.notify();
			};
			this.requestFn()
			.then(set)
			.catch(set);
		}
		return this.value;
	}
}

// For a list of sources, this will subscribe to all of them and call a mapping
// function when either of them has a new value.
class Combine extends Base {
	constructor(sources, fn) {
		super();
		this.sources = Array.isArray(sources) ? sources : [sources];
		this.mapFn = fn;
		this.notify = this.notify.bind(this);
	}
	get() {
		return this.mapFn(...this.sources.map(source => source.get()));
	}
	bind() {
		for (const source of this.sources) {
			source.on(this.notify, false);
		}
	}
	unbind() {
		for (const source of this.sources) {
			source.off(this.notify);
		}
	}
}

class FlatMap extends Base {
	constructor(source, fn) {
		super();
		this.source = source;
		this.child = null;
		this.notify = this.notify.bind(this);
		this.listener = child => {
			if (this.child) {
				this.child.off(this.notify);
			}
			this.child = fn(child);
			if (this.child) {
				this.child.on(this.notify);
			}
		};
	}
	get() {
		return this.child && this.child.get();
	}
	bind() {
		this.source.on(this.listener);
	}
	unbind() {
		if (this.child) {
			this.child.off(this.notify);
		}
		this.source.off(this.listener);
	}
}

export default {
	new: (...args) => new Property(...args),
	switch: (...args) => new Switch(...args),
	lazy: (...args) => new Lazy(...args),
	combine: (...args) => new Combine(...args),
	flatMap: (...args) => new FlatMap(...args),
	true: new Property(true),
	false: new Property(false),
};



// WEBPACK FOOTER //
// ./lib/pagehandler/utils/frp.js